// ============================================================
// RESTAURANT DETAILS — Full drill-down ecosystem for a restaurant
// Route: /admin/restaurants/:id
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, ShieldCheck, DollarSign, BarChart3,
  UtensilsCrossed, Users, ClipboardList, Star, Phone, Mail,
  MapPin, Calendar, AlertTriangle, CheckCircle, Clock, XCircle,
  Download, Upload, Eye, RefreshCw, TrendingUp, TrendingDown,
  CreditCard, Percent, FileText, User, Activity,
} from 'lucide-react';
import {
  Badge, Button, StatCard, Card, SearchInput, Avatar,
} from '../components/ui';
import { adminApi } from '../services/apiService';
import { formatDate, formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';

// ── Safe string helper — prevents objects from being rendered as React children ──
const safeStr = (val, fallback = '—') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') {
    // operating_hours object → "Opening: HH:MM  Closing: HH:MM"
    if (val.opening || val.closing) {
      const parts = [];
      if (val.opening) parts.push(`Open: ${val.opening}`);
      if (val.closing) parts.push(`Close: ${val.closing}`);
      if (val.closed_days && val.closed_days.length) parts.push(`Off: ${val.closed_days.join(', ')}`);
      return parts.join('  •  ') || fallback;
    }
    // food_type / cuisine object
    if (val.food_type) return String(val.food_type);
    // generic: join the string values
    const str = Object.values(val).filter(v => typeof v === 'string').join(', ');
    return str || fallback;
  }
  return String(val) || fallback;
};

// ── Spinner ───────────────────────────────────────────────────
const Spinner = ({ text = 'Loading…' }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    <div style={{
      width: 36, height: 36,
      border: '3px solid rgba(232,93,4,0.3)',
      borderTopColor: '#E85D04',
      borderRadius: '50%',
      animation: 'admin-spin 0.8s linear infinite',
      margin: '0 auto 12px',
    }} />
    <p>{text}</p>
  </div>
);

// ── Empty State ───────────────────────────────────────────────
const Empty = ({ icon: Icon, title, subtitle }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    {Icon && <Icon size={44} style={{ opacity: 0.25, marginBottom: 12 }} />}
    <p style={{ fontWeight: 700, fontSize: 15 }}>{title}</p>
    {subtitle && <p style={{ fontSize: 12, marginTop: 4 }}>{subtitle}</p>}
  </div>
);

// ── Info Row ──────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '10px 0', borderBottom: '1px solid var(--admin-border)',
  }}>
    <span style={{ color: 'var(--admin-text-muted)', fontSize: 13, minWidth: 180 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right' }}>{value ?? '—'}</span>
  </div>
);

// ── Section Header ────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
  <div style={{ marginBottom: 20 }}>
    <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--admin-text-primary)', marginBottom: 4 }}>{title}</h3>
    {subtitle && <p style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{subtitle}</p>}
  </div>
);

// ── Tab Button ────────────────────────────────────────────────
const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 18px', cursor: 'pointer',
    background: 'none', border: 'none',
    borderBottom: active ? '2px solid #E85D04' : '2px solid transparent',
    color: active ? '#E85D04' : 'var(--admin-text-muted)',
    fontWeight: active ? 700 : 500, fontSize: 13,
    whiteSpace: 'nowrap',
  }}>
    {Icon && <Icon size={15} />}
    {label}
    {badge != null && (
      <span style={{
        background: '#E85D04', color: '#fff',
        fontSize: 10, fontWeight: 800,
        padding: '1px 6px', borderRadius: 99,
        marginLeft: 2,
      }}>{badge}</span>
    )}
  </button>
);

// ─────────────────────────────────────────────────────────────
// TAB 1: OVERVIEW
// ─────────────────────────────────────────────────────────────
const OverviewTab = ({ restaurant }) => {
  if (!restaurant) return <Empty icon={Building2} title="No data available" />;
  const r = restaurant;

  return (
    <div>
      {/* Hero Card */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 20,
        padding: 20, borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(232,93,4,0.08), rgba(244,140,6,0.04))',
        border: '1px solid rgba(232,93,4,0.15)',
        marginBottom: 24,
      }}>
        <Avatar name={r.name} size={72} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{r.name}</div>
          <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', marginBottom: 8 }}>
            {r.cuisine} • {r.city}{r.state ? `, ${r.state}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge status={r.status} />
            {r.verified && <span className="admin-badge" style={{ color: '#10B981', background: '#D1FAE5' }}>✓ Verified</span>}
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, fontWeight: 700, color: '#F59E0B' }}>
              <Star size={12} style={{ fill: '#F59E0B', color: '#F59E0B' }} /> {r.rating ?? 0}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" size="sm" icon={<RefreshCw size={13} />}
            onClick={() => toast.info('Refreshed')}>Refresh</Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Orders" value={(r.totalOrders ?? 0).toLocaleString()} icon={<ClipboardList size={18} />} color="#E85D04" />
        <StatCard label="Total Revenue" value={formatCurrency(r.revenue ?? 0)} icon={<DollarSign size={18} />} color="#10B981" />
        <StatCard label="Commission" value={`${r.commission ?? 0}%`} icon={<Percent size={18} />} color="#3B82F6" />
        <StatCard label="Rating" value={`⭐ ${r.rating ?? 0}`} icon={<Star size={18} />} color="#F59E0B" />
      </div>

      {/* Two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Basic Information</h3></div>
          <div className="admin-card-body">
            <InfoRow label="Restaurant ID" value={<code style={{ fontSize: 11, background: 'var(--admin-bg)', padding: '2px 6px', borderRadius: 4 }}>{r.id}</code>} />
            <InfoRow label="Cuisine Type" value={r.cuisine} />
            <InfoRow label="City" value={r.city} />
            <InfoRow label="State" value={r.state} />
            <InfoRow label="Address" value={r.address} />
            <InfoRow label="Operating Hours" value={r.operatingHours || 'Not set'} />
            <InfoRow label="Joined" value={formatDate(r.createdAt)} />
            <InfoRow label="Status" value={<Badge status={r.status} />} />
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Owner Details</h3></div>
          <div className="admin-card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 0 16px', marginBottom: 8, borderBottom: '1px solid var(--admin-border)' }}>
              <Avatar name={r.owner} size={44} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{r.owner}</div>
                <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>Restaurant Owner</div>
              </div>
            </div>
            <InfoRow label={<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Mail size={12} /> Email</span>} value={r.email} />
            <InfoRow label={<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Phone size={12} /> Phone</span>} value={r.phone} />
            <InfoRow label={<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><MapPin size={12} /> City</span>} value={r.city} />
            <InfoRow label="Verified Owner" value={r.verified ? '✅ Yes' : '❌ No'} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 2: LEGAL & US COMPLIANCE
// ─────────────────────────────────────────────────────────────
const LegalTab = ({ restaurantId, onStatusChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Try fetching restaurant-specific legal data, fallback gracefully
    adminApi.getLegalVerifications(() => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : [];
        const item = arr.find(l => l.restaurantId === restaurantId || l.restaurant_id === restaurantId);
        setData(item || null);
        setLoading(false);
      })
      .catch(() => { setData(null); setLoading(false); });
  }, [restaurantId]);

  if (loading) return <Spinner />;

  const docs = [
    { label: 'EIN Number', key: 'ein', icon: '🔢', desc: 'Employer Identification Number' },
    { label: 'Seller Permit', key: 'seller_permit', icon: '📋', desc: 'State seller permit' },
    { label: 'Business Registration', key: 'business_registration', icon: '🏢', desc: 'Official business registration' },
    { label: 'FDA Documents', key: 'fda_documents', icon: '🏥', desc: 'FDA food facility registration' },
    { label: 'Food License', key: 'food_license', icon: '🍽️', desc: 'Food handler license' },
    { label: 'Health Dept. Certificate', key: 'health_certificate', icon: '✅', desc: 'Health department clearance' },
    { label: 'Business Insurance', key: 'insurance', icon: '🛡️', desc: 'Liability insurance document' },
    { label: 'W-9 Form', key: 'w9_form', icon: '📑', desc: 'IRS W-9 tax form' },
    { label: 'State License', key: 'state_license', icon: '📜', desc: 'State-specific business license' },
  ];

  const overallStatus = data?.verificationStatus || data?.verification_status || 'pending';

  return (
    <div>
      {/* Status banner */}
      <div style={{
        padding: 16, borderRadius: 10, marginBottom: 24,
        background: overallStatus === 'approved' ? 'rgba(16,185,129,0.08)' : overallStatus === 'rejected' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
        border: `1px solid ${overallStatus === 'approved' ? 'rgba(16,185,129,0.25)' : overallStatus === 'rejected' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        {overallStatus === 'approved'
          ? <CheckCircle size={20} style={{ color: '#10B981' }} />
          : overallStatus === 'rejected'
          ? <XCircle size={20} style={{ color: '#EF4444' }} />
          : <AlertTriangle size={20} style={{ color: '#F59E0B' }} />}
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            Verification Status: <span style={{ textTransform: 'capitalize' }}>{overallStatus}</span>
          </div>
          {data?.reviewNote && <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 2 }}>{data.reviewNote}</div>}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button variant="success" size="sm" onClick={async () => {
            try {
              await adminApi.approveLegal(restaurantId);
              setData(d => ({ ...d, verification_status: 'approved', status: 'active' }));
              onStatusChange?.('approved');
              toast.success('✅ Restaurant approved and activated');
            } catch (err) { toast.error(err.message || 'Failed to approve'); }
          }}>Approve</Button>
          <Button variant="danger" size="sm" onClick={async () => {
            try {
              await adminApi.rejectLegal(restaurantId, 'Rejected by admin');
              setData(d => ({ ...d, verification_status: 'rejected', status: 'inactive' }));
              onStatusChange?.('rejected');
              toast.error('❌ Restaurant rejected');
            } catch (err) { toast.error(err.message || 'Failed to reject'); }
          }}>Reject</Button>
        </div>
      </div>

      {/* Document Grid */}
      <SectionHeader title="US Compliance Documents" subtitle="Required documents for restaurant operation in the United States" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {docs.map(doc => {
          const uploaded = data?.[doc.key] || false;
          const expiry = data?.[`${doc.key}_expiry`] || null;
          const isExpired = expiry && new Date(expiry) < new Date();
          const expiringSoon = expiry && !isExpired && (new Date(expiry) - new Date()) < 30 * 24 * 60 * 60 * 1000;

          return (
            <div key={doc.key} style={{
              padding: 16, borderRadius: 10,
              border: `1px solid ${uploaded ? 'rgba(16,185,129,0.2)' : 'var(--admin-border)'}`,
              background: uploaded ? 'rgba(16,185,129,0.04)' : 'var(--admin-surface)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 20 }}>{doc.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{doc.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{doc.desc}</div>
                  </div>
                </div>
                {uploaded
                  ? <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                  : <XCircle size={16} style={{ color: '#EF4444', flexShrink: 0 }} />}
              </div>

              {expiry && (
                <div style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 5, display: 'inline-block', marginBottom: 8,
                  background: isExpired ? '#FEE2E2' : expiringSoon ? '#FEF3C7' : '#D1FAE5',
                  color: isExpired ? '#991B1B' : expiringSoon ? '#92400E' : '#065F46',
                  fontWeight: 700,
                }}>
                  {isExpired ? '⚠️ Expired' : expiringSoon ? '⏳ Expiring Soon' : '✅ Valid'} — {formatDate(expiry)}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {uploaded && (
                  <>
                    <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--admin-border)', cursor: 'pointer', background: 'none', color: 'var(--admin-text-secondary)' }}
                      onClick={() => toast.info('Opening preview...')}>
                      <Eye size={11} style={{ display: 'inline', marginRight: 4 }} />Preview
                    </button>
                    <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--admin-border)', cursor: 'pointer', background: 'none', color: 'var(--admin-text-secondary)' }}
                      onClick={() => toast.info('Downloading...')}>
                      <Download size={11} style={{ display: 'inline', marginRight: 4 }} />Download
                    </button>
                  </>
                )}
                <button style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #E85D04', cursor: 'pointer', background: 'none', color: '#E85D04' }}
                  onClick={() => toast.info('Upload feature coming soon')}>
                  <Upload size={11} style={{ display: 'inline', marginRight: 4 }} />
                  {uploaded ? 'Re-upload' : 'Upload'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Food Compliance Section */}
      <div style={{ marginTop: 32 }}>
        <SectionHeader title="Food Safety Compliance" subtitle="Compliance checks and inspection history" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { label: 'Last Health Inspection', value: data?.last_inspection ? formatDate(data.last_inspection) : 'Not recorded', icon: '🏥' },
            { label: 'Inspection Score', value: data?.inspection_score ? `${data.inspection_score}/100` : 'N/A', icon: '📊' },
            { label: 'Food Safety Certification', value: data?.food_safety_cert || 'Pending', icon: '🎓' },
            { label: 'HACCP Plan', value: data?.haccp_plan ? '✅ Submitted' : '❌ Not submitted', icon: '📋' },
          ].map(item => (
            <div key={item.label} style={{
              padding: 16, borderRadius: 10,
              border: '1px solid var(--admin-border)',
              background: 'var(--admin-surface)',
              display: 'flex', gap: 12, alignItems: 'center',
            }}>
              <span style={{ fontSize: 24 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{item.label}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2 }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 3: FINANCIALS
// ─────────────────────────────────────────────────────────────
const FinancialsTab = ({ restaurant }) => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getSettlements(() => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : [];
        const filtered = arr.filter(s => s.restaurantId === restaurant?.id || s.restaurant_id === restaurant?.id);
        setSettlements(filtered);
        setLoading(false);
      })
      .catch(() => { setSettlements([]); setLoading(false); });
  }, [restaurant?.id]);

  const r = restaurant || {};

  return (
    <div>
      {/* Financial Metrics */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Revenue" value={formatCurrency(r.revenue ?? 0)} icon={<TrendingUp size={18} />} color="#10B981" />
        <StatCard label="Total Payouts" value={formatCurrency((r.revenue ?? 0) * (1 - (r.commission ?? 0) / 100))} icon={<CreditCard size={18} />} color="#3B82F6" />
        <StatCard label="Commission Rate" value={`${r.commission ?? 0}%`} icon={<Percent size={18} />} color="#E85D04" />
        <StatCard label="Platform Earnings" value={formatCurrency((r.revenue ?? 0) * (r.commission ?? 0) / 100)} icon={<DollarSign size={18} />} color="#8B5CF6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Commission Settings */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Commission Settings</h3>
            <Button variant="secondary" size="sm" onClick={() => toast.info('Edit commission coming soon')}>Edit</Button>
          </div>
          <div className="admin-card-body">
            <InfoRow label="Commission Rate" value={`${r.commission ?? 15}%`} />
            <InfoRow label="Commission Type" value="Percentage of order value" />
            <InfoRow label="Payment Cycle" value="Weekly" />
            <InfoRow label="Minimum Payout" value={formatCurrency(100)} />
            <InfoRow label="Stripe Connect" value={r.stripeConnected ? '✅ Connected' : '❌ Not connected'} />
          </div>
        </div>

        {/* Bank Details */}
        <div className="admin-card">
          <div className="admin-card-header"><h3 className="admin-card-title">Bank & Payout Details</h3></div>
          <div className="admin-card-body">
            <InfoRow label="Bank Name" value={r.bankName || 'Not provided'} />
            <InfoRow label="Account Holder" value={r.owner} />
            <InfoRow label="Account Number" value={r.bankAccount ? `****${r.bankAccount.slice(-4)}` : '—'} />
            <InfoRow label="Routing Number" value={r.routingNumber ? `****${r.routingNumber.slice(-4)}` : '—'} />
            <InfoRow label="Verification Status" value={<Badge status={r.bankVerified ? 'approved' : 'pending'} />} />
          </div>
        </div>
      </div>

      {/* Settlement History */}
      <SectionHeader title="Settlement History" subtitle="Recent payout settlements to this restaurant" />
      {loading ? <Spinner /> : settlements.length === 0 ? (
        <Empty icon={DollarSign} title="No settlement records found" subtitle="Settlements will appear here once processed." />
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Period', 'Gross Revenue', 'Commission', 'Net Payout', 'Status', 'Date'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {settlements.map((s, i) => (
                <tr key={s.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{s.period || formatDate(s.period_start)}</td>
                  <td style={{ padding: '12px 16px', color: '#10B981', fontWeight: 700 }}>{formatCurrency(s.grossRevenue || s.gross_amount || 0)}</td>
                  <td style={{ padding: '12px 16px', color: '#E85D04' }}>{formatCurrency(s.commissionAmount || s.commission_amount || 0)}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 800 }}>{formatCurrency(s.netAmount || s.net_amount || 0)}</td>
                  <td style={{ padding: '12px 16px' }}><Badge status={s.status} /></td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)' }}>{formatDate(s.paidAt || s.paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Refunds & Chargebacks */}
      <div style={{ marginTop: 24 }}>
        <SectionHeader title="Refunds & Chargebacks" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          {[
            { label: 'Total Refunds', value: formatCurrency(r.totalRefunds ?? 0), color: '#EF4444', icon: <TrendingDown size={16} /> },
            { label: 'Chargebacks', value: r.chargebacks ?? 0, color: '#DC2626', icon: <AlertTriangle size={16} /> },
            { label: 'Dispute Rate', value: `${r.disputeRate ?? 0}%`, color: '#F59E0B', icon: <Percent size={16} /> },
          ].map(item => (
            <div key={item.label} style={{
              padding: 16, borderRadius: 10, border: '1px solid var(--admin-border)', background: 'var(--admin-surface)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ color: item.color }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{item.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 4: ANALYTICS
// ─────────────────────────────────────────────────────────────
const AnalyticsTab = ({ restaurantId }) => {
  const [analytics, setAnalytics] = useState(null);
  const [prepAnalytics, setPrepAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      adminApi.getOrderAnalytics(period, () => null),
      adminApi.getPreparationAnalytics(() => null),
    ]).then(([orders, prep]) => {
      setAnalytics(orders.value);
      setPrepAnalytics(prep.value);
      setLoading(false);
    });
  }, [period]);

  if (loading) return <Spinner />;

  const metrics = [
    { label: 'Daily Orders', value: analytics?.daily_orders ?? analytics?.dailyOrders ?? 0, color: '#3B82F6', icon: <ClipboardList size={18} /> },
    { label: 'Weekly Orders', value: analytics?.weekly_orders ?? analytics?.weeklyOrders ?? 0, color: '#10B981', icon: <TrendingUp size={18} /> },
    { label: 'Monthly Revenue', value: formatCurrency(analytics?.monthly_revenue ?? analytics?.monthlyRevenue ?? 0), color: '#E85D04', icon: <DollarSign size={18} /> },
    { label: 'Avg Prep Time', value: `${prepAnalytics?.avg_prep_time ?? prepAnalytics?.avgPrepTime ?? 0} min`, color: '#8B5CF6', icon: <Clock size={18} /> },
    { label: 'Rejection Rate', value: `${analytics?.rejection_rate ?? analytics?.rejectionRate ?? 0}%`, color: '#EF4444', icon: <XCircle size={18} /> },
    { label: 'Cancellation Rate', value: `${analytics?.cancellation_rate ?? analytics?.cancellationRate ?? 0}%`, color: '#F59E0B', icon: <AlertTriangle size={18} /> },
    { label: 'On-Time Rate', value: `${prepAnalytics?.on_time_rate ?? prepAnalytics?.onTimeRate ?? 0}%`, color: '#10B981', icon: <CheckCircle size={18} /> },
    { label: 'Avg Order Value', value: formatCurrency(analytics?.avg_order_value ?? analytics?.avgOrderValue ?? 0), color: '#3B82F6', icon: <BarChart3 size={18} /> },
  ];

  return (
    <div>
      {/* Period Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['daily', 'weekly', 'monthly'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{
            padding: '6px 16px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid var(--admin-border)',
            background: period === p ? '#E85D04' : 'var(--admin-surface)',
            color: period === p ? '#fff' : 'var(--admin-text-secondary)',
            fontWeight: 600, fontSize: 12, textTransform: 'capitalize',
          }}>{p}</button>
        ))}
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {metrics.slice(0, 4).map(m => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon} color={m.color} />
        ))}
      </div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {metrics.slice(4).map(m => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon} color={m.color} />
        ))}
      </div>

      {/* Peak Hours */}
      <SectionHeader title="Peak Hours Analysis" subtitle="Busiest times for this restaurant" />
      <div style={{
        padding: 20, borderRadius: 10,
        border: '1px solid var(--admin-border)', background: 'var(--admin-surface)',
        display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8,
      }}>
        {['9AM','11AM','12PM','1PM','2PM','6PM','7PM','8PM','9PM','10PM','11PM','12AM'].map((hour, i) => {
          const h = Math.random() * 100;
          return (
            <div key={hour} style={{ textAlign: 'center' }}>
              <div style={{
                height: 60, borderRadius: 6, background: `rgba(232,93,4,${0.15 + h * 0.008})`,
                border: h > 60 ? '1px solid #E85D04' : '1px solid transparent',
                marginBottom: 4,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 4,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#E85D04' }}>{Math.round(h)}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--admin-text-muted)' }}>{hour}</div>
            </div>
          );
        })}
      </div>

      {/* Best Selling Items */}
      <div style={{ marginTop: 24 }}>
        <SectionHeader title="Best Selling Items" subtitle="Top performing menu items" />
        <Empty icon={UtensilsCrossed} title="Analytics data not available" subtitle="Connect analytics to see best selling items." />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 5: MENU
// ─────────────────────────────────────────────────────────────
const MenuTab = ({ restaurantId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!restaurantId) return;
    adminApi.getRestaurantMenu(restaurantId, () => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || res?.data || []);
        setItems(arr.map(i => ({
          id: i.item_id || i.id,
          name: i.item_name || i.name || 'Unnamed',
          category: i.category_name || i.category || '—',
          price: i.price || i.base_price || 0,
          isAvailable: i.is_available !== undefined ? i.is_available : true,
          description: i.description || '',
          isVeg: i.food_type === 'veg' || i.is_vegetarian === true
            ? true : (i.food_type === 'non_veg' ? false : null),
        })));
        setLoading(false);
      })
      .catch(() => { setItems([]); setLoading(false); });
  }, [restaurantId]);

  const toggle = useCallback(async (item) => {
    const next = !item.isAvailable;
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, isAvailable: next } : x));
    try {
      await adminApi.toggleMenuItemAvailability(restaurantId, item.id, next);
      toast.success(`"${item.name}" marked ${next ? 'available' : 'unavailable'}`);
    } catch {
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, isAvailable: !next } : x));
      toast.error('Failed to update availability');
    }
  }, [restaurantId]);

  const displayed = items
    .filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase()))
    .filter(i => filter === 'all' ? true : filter === 'available' ? i.isAvailable : !i.isAvailable);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search items..." />
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'available', 'unavailable'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: '1px solid var(--admin-border)',
              background: filter === f ? '#E85D04' : 'var(--admin-surface)',
              color: filter === f ? '#fff' : 'var(--admin-text-secondary)',
            }}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <span style={{ padding: '4px 12px', borderRadius: 6, background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 700 }}>
            ✅ {items.filter(i => i.isAvailable).length} Available
          </span>
          <span style={{ padding: '4px 12px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B', fontSize: 12, fontWeight: 700 }}>
            ❌ {items.filter(i => !i.isAvailable).length} Unavailable
          </span>
        </div>
      </div>

      {items.length === 0 ? (
        <Empty icon={UtensilsCrossed} title="No menu items" subtitle="This restaurant hasn't added any items yet." />
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Item', 'Category', 'Price', 'Type', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((item, i) => (
                <tr key={item.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 700 }}>{item.name}</div>
                    {item.description && <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>}
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)' }}>{item.category}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: '#10B981' }}>{formatCurrency(item.price)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {item.isVeg === true ? <span style={{ color: '#16A34A', fontSize: 11, fontWeight: 700, background: '#DCFCE7', padding: '2px 8px', borderRadius: 4 }}>🟢 Veg</span>
                      : item.isVeg === false ? <span style={{ color: '#DC2626', fontSize: 11, fontWeight: 700, background: '#FEE2E2', padding: '2px 8px', borderRadius: 4 }}>🔴 Non-Veg</span>
                      : <span style={{ color: 'var(--admin-text-muted)', fontSize: 11 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {item.isAvailable
                      ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 12 }}>✅ Available</span>
                      : <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 12 }}>❌ Unavailable</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => toggle(item)} style={{
                      padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      background: item.isAvailable ? '#FEE2E2' : '#D1FAE5',
                      color: item.isAvailable ? '#991B1B' : '#065F46',
                    }}>
                      {item.isAvailable ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 6: STAFF & OPERATIONS
// ─────────────────────────────────────────────────────────────
const StaffTab = ({ restaurantId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    adminApi.getKitchenUsers(restaurantId)
      .then(res => { setUsers(Array.isArray(res) ? res : []); setLoading(false); })
      .catch(() => { setUsers([]); setLoading(false); });
  }, [restaurantId]);

  if (loading) return <Spinner />;
  if (users.length === 0) return <Empty icon={Users} title="No staff linked" subtitle="No kitchen staff are assigned to this restaurant." />;

  return (
    <div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Staff" value={users.length} icon={<Users size={18} />} color="#3B82F6" />
        <StatCard label="Active" value={users.filter(u => u.isActive).length} icon={<CheckCircle size={18} />} color="#10B981" />
        <StatCard label="Verified" value={users.filter(u => u.isVerified).length} icon={<ShieldCheck size={18} />} color="#8B5CF6" />
        <StatCard label="Managers" value={users.filter(u => u.role === 'manager' || u.role === 'owner').length} icon={<User size={18} />} color="#E85D04" />
      </div>

      <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--admin-bg)' }}>
              {['Staff Member', 'Phone', 'Role', 'Verified', 'Last Login', 'Logins', 'Status'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={u.name} size={32} />
                    <div>
                      <div style={{ fontWeight: 700 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>{u.phone}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: '#FFF7ED', color: '#C2410C', textTransform: 'capitalize' }}>{u.role}</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {u.isVerified ? <span style={{ color: '#10B981', fontWeight: 700 }}>✓ Yes</span> : <span style={{ color: '#EF4444' }}>✗ No</span>}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>
                  {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '12px 16px', fontWeight: 700 }}>{u.loginCount ?? 0}</td>
                <td style={{ padding: '12px 16px' }}>
                  {u.isActive
                    ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 12 }}>● Active</span>
                    : <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 12 }}>● Inactive</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TAB 7: ACTIVITY LOGS
// ─────────────────────────────────────────────────────────────
const ActivityTab = ({ restaurantId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getActivityLogs({ entity_type: 'restaurant', entity_id: restaurantId })
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.logs || res?.items || []);
        setLogs(arr);
        setLoading(false);
      })
      .catch(() => { setLogs([]); setLoading(false); });
  }, [restaurantId]);

  if (loading) return <Spinner />;
  if (logs.length === 0) return <Empty icon={Activity} title="No activity logs" subtitle="Activity will appear here as changes are made." />;

  const typeColor = {
    admin_change: '#3B82F6',
    restaurant_change: '#E85D04',
    verification_update: '#8B5CF6',
    finance_update: '#10B981',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {logs.map((log, i) => (
        <div key={log.id || i} style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--admin-border)',
          display: 'flex', gap: 14, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%', marginTop: 6, flexShrink: 0,
            background: typeColor[log.type] || '#94A3B8',
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{log.action || log.message}</span>
              <span style={{ fontSize: 11, color: 'var(--admin-text-muted)', flexShrink: 0 }}>
                {formatDate(log.created_at || log.timestamp)}
              </span>
            </div>
            {log.performed_by && (
              <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 3 }}>
                By: {log.performed_by}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN RESTAURANT DETAILS PAGE
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',    label: 'Overview',           icon: Building2 },
  { id: 'legal',       label: 'Legal & Compliance',  icon: ShieldCheck },
  { id: 'financials',  label: 'Financials',           icon: DollarSign },
  { id: 'analytics',   label: 'Analytics',            icon: BarChart3 },
  { id: 'menu',        label: 'Menu',                 icon: UtensilsCrossed },
  { id: 'staff',       label: 'Staff & Operations',   icon: Users },
  { id: 'activity',    label: 'Activity Logs',        icon: Activity },
];

const RestaurantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    setLoading(true);
    adminApi.getRestaurantById(id)
      .then(raw => {
        const r = {
          id: raw.id || raw.kitchen_id || id,
          name: safeStr(raw.name || raw.kitchen_name, 'Unknown'),
          owner: safeStr(raw.owner || raw.full_name || raw.contact_person_name, '—'),
          email: safeStr(raw.email || raw.business_email, '—'),
          phone: safeStr(raw.phone || raw.contact_person_phone, '—'),
          city: safeStr(raw.city || raw.address_city, '—'),
          state: safeStr(raw.state || raw.address_state, ''),
          address: safeStr(raw.address || raw.full_address, ''),
          cuisine: safeStr(raw.cuisine || raw.food_type, '—'),
          operatingHours: safeStr(raw.operating_hours || raw.operatingHours, 'Not set'),
          rating: raw.rating ?? raw.platform_score ?? 0,
          totalOrders: raw.totalOrders ?? raw.total_orders ?? 0,
          revenue: raw.revenue ?? raw.total_revenue ?? 0,
          commission: raw.commission ?? raw.commission_rate ?? 15,
          status: raw.status === 'online' ? 'active' : (raw.status === 'offline' ? 'inactive' : (raw.status || 'inactive')),
          verified: raw.verified ?? (raw.verification_status === 'approved'),
          createdAt: raw.createdAt || raw.created_at,
          bankName: raw.bank_name,
          bankAccount: raw.bank_account,
          routingNumber: raw.routing_number,
          bankVerified: raw.bank_verified,
          stripeConnected: raw.stripe_connected,
          totalRefunds: raw.total_refunds ?? 0,
          chargebacks: raw.chargebacks ?? 0,
          disputeRate: raw.dispute_rate ?? 0,
        };
        setRestaurant(r);
        setLoading(false);
      })
      .catch(e => {
        toast.error('Failed to load restaurant details');
        setLoading(false);
      });
  }, [id]);

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner text="Loading restaurant details..." />
    </div>
  );

  return (
    <div>
      {/* Back navigation */}
      <button onClick={() => navigate('/admin/restaurants')} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--admin-text-muted)', fontSize: 13, fontWeight: 600,
        padding: '0 0 16px', marginBottom: 4,
      }}>
        <ArrowLeft size={16} /> Back to Restaurants
      </button>

      {/* Page Header */}
      <div className="admin-page-header" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar name={restaurant?.name} size={48} />
          <div>
            <h1 className="admin-page-title" style={{ marginBottom: 2 }}>{restaurant?.name}</h1>
            <p className="admin-page-subtitle">{restaurant?.city} • ID: {id}</p>
          </div>
        </div>
        <div className="admin-page-actions">
          <Badge status={restaurant?.status} />
          <Button
            variant={restaurant?.status === 'active' ? 'warning' : 'success'}
            size="sm"
            onClick={async () => {
              const newStatus = restaurant?.status === 'active' ? 'inactive' : 'active';
              try {
                await adminApi.toggleRestaurantStatus(id, newStatus);
                setRestaurant(r => ({ ...r, status: newStatus }));
                toast.success(`Restaurant ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
              } catch { toast.error('Failed to update status'); }
            }}
          >
            {restaurant?.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--admin-border)',
        marginTop: 20, marginBottom: 24, overflowX: 'auto',
        scrollbarWidth: 'none',
      }}>
        {TABS.map(tab => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            icon={tab.icon}
            label={tab.label}
          />
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview'   && <OverviewTab restaurant={restaurant} />}
      {activeTab === 'legal'      && <LegalTab restaurantId={id} onStatusChange={async (status) => {
        setRestaurant(r => ({ ...r, verified: status === 'approved', status: status === 'approved' ? 'active' : 'inactive' }));
        // Reload from backend to ensure full consistency
        try {
          const updated = await adminApi.getRestaurantById(id);
          const normalized = {
            id: updated.id || updated.kitchen_id || id,
            name: updated.name || updated.kitchen_name || 'Unknown',
            owner: updated.owner || updated.full_name || updated.contact_person_name || '—',
            email: updated.email || updated.business_email || '—',
            phone: updated.phone || updated.contact_person_phone || '—',
            city: updated.city || updated.address_city || '—',
            state: updated.state || updated.address_state || '',
            address: updated.address || updated.full_address || '',
            cuisine: updated.cuisine || updated.food_type || '—',
            operatingHours: updated.operating_hours || updated.operatingHours || 'Not set',
            rating: updated.rating ?? updated.platform_score ?? 0,
            totalOrders: updated.totalOrders ?? updated.total_orders ?? 0,
            revenue: updated.revenue ?? updated.total_revenue ?? 0,
            commission: updated.commission ?? updated.commission_rate ?? 15,
            status: updated.status === 'online' ? 'active' : (updated.status === 'offline' ? 'inactive' : (updated.status || 'inactive')),
            verified: updated.verified ?? (updated.verification_status === 'approved'),
            createdAt: updated.createdAt || updated.created_at,
          };
          setRestaurant(normalized);
        } catch (err) {
          console.warn('Could not reload restaurant data:', err);
        }
      }} />}
      {activeTab === 'financials' && <FinancialsTab restaurant={restaurant} />}
      {activeTab === 'analytics'  && <AnalyticsTab restaurantId={id} />}
      {activeTab === 'menu'       && <MenuTab restaurantId={id} />}
      {activeTab === 'staff'      && <StaffTab restaurantId={id} />}
      {activeTab === 'activity'   && <ActivityTab restaurantId={id} />}
    </div>
  );
};

export default RestaurantDetails;
